import React, { useState } from 'react'
import StatusChip from '../../components/ui/StatusChip'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Block as DeactivateIcon,
  CheckCircle as ActivateIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import {
  useVendors,
  useCreateVendor,
  useUpdateVendor,
  useToggleVendorActive,
} from '../../hooks/api/useVendors'
import { Vendor, CreateVendorForm } from '../../types'

const emptyForm = (): CreateVendorForm => ({
  name: '',
  phone: '',
  address: '',
  ntn: '',
  strn: '',
})

const VendorFormDialog: React.FC<{
  open: boolean
  onClose: () => void
  vendor?: Vendor | null
}> = ({ open, onClose, vendor }) => {
  const [form, setForm] = useState<CreateVendorForm>(emptyForm())
  const createMutation = useCreateVendor()
  const updateMutation = useUpdateVendor()

  React.useEffect(() => {
    if (open) {
      setForm(
        vendor
          ? { name: vendor.name, phone: vendor.phone, address: vendor.address, ntn: vendor.ntn, strn: vendor.strn }
          : emptyForm()
      )
    }
  }, [open, vendor])

  const isPending = createMutation.isPending || updateMutation.isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) return
    try {
      if (vendor) {
        await updateMutation.mutateAsync({ id: vendor._id, data: form })
      } else {
        await createMutation.mutateAsync(form)
      }
      onClose()
    } catch {
      // handled by mutation onError
    }
  }

  const field = (
    label: string,
    key: keyof CreateVendorForm,
    required = false,
    xs: number = 12,
    sm: number = 6
  ) => (
    <Grid item xs={xs} sm={sm}>
      <TextField
        fullWidth
        required={required}
        label={label}
        value={(form[key] as string) ?? ''}
        onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
        size="small"
        disabled={isPending}
      />
    </Grid>
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{vendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {field('Vendor Name *', 'name', true, 12, 12)}
            {field('Phone', 'phone', false, 12, 6)}
            {field('NTN', 'ntn', false, 6, 3)}
            {field('STRN', 'strn', false, 6, 3)}
            {field('Address', 'address', false, 12, 12)}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isPending || !form.name.trim()}
            startIcon={isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {isPending ? 'Saving...' : vendor ? 'Save Changes' : 'Add Vendor'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

const VendorsPage: React.FC = () => {
  const [search, setSearch] = useState('')
  const [showInactive, setShowInactive] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null)
  const [confirmDeactivate, setConfirmDeactivate] = useState<Vendor | null>(null)

  const { data: vendors = [], isLoading, isError, refetch } = useVendors({
    search: search || undefined,
    active_only: !showInactive,
  })

  const toggleActiveMutation = useToggleVendorActive()

  const openCreate = () => {
    setEditingVendor(null)
    setFormOpen(true)
  }

  const openEdit = (vendor: Vendor) => {
    setEditingVendor(vendor)
    setFormOpen(true)
  }

  const handleToggleActive = async (vendor: Vendor) => {
    if (vendor.is_active) {
      setConfirmDeactivate(vendor)
    } else {
      await toggleActiveMutation.mutateAsync(vendor._id)
    }
  }

  const confirmDeactivateAction = async () => {
    if (confirmDeactivate) {
      await toggleActiveMutation.mutateAsync(confirmDeactivate._id)
      setConfirmDeactivate(null)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Vendors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your vendor list. Vendors can be selected when creating purchase invoices.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Add Vendor
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              size="small"
              label="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vendors..."
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Button
              fullWidth
              variant={showInactive ? 'contained' : 'outlined'}
              color="warning"
              size="small"
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive ? 'Showing All' : 'Show Inactive'}
            </Button>
          </Grid>
          <Grid item xs={6} sm={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load vendors.
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>NTN</TableCell>
                <TableCell>STRN</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Added</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : vendors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No vendors found. Add your first vendor to get started.
                  </TableCell>
                </TableRow>
              ) : (
                vendors.map((vendor) => (
                  <TableRow
                    key={vendor._id}
                    sx={{ opacity: vendor.is_active ? 1 : 0.55 }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {vendor.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{vendor.phone || '—'}</TableCell>
                    <TableCell>{vendor.ntn || '—'}</TableCell>
                    <TableCell>{vendor.strn || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {vendor.address || '—'}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        label={vendor.is_active ? 'Active' : 'Inactive'}
                        variant={vendor.is_active ? 'success' : 'neutral'}
                      />
                    </TableCell>
                    <TableCell>
                      {format(new Date(vendor.createdAt), 'dd MMM yyyy')}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(vendor)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={vendor.is_active ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          color={vendor.is_active ? 'warning' : 'success'}
                          onClick={() => handleToggleActive(vendor)}
                        >
                          {vendor.is_active ? (
                            <DeactivateIcon fontSize="small" />
                          ) : (
                            <ActivateIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit dialog */}
      <VendorFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        vendor={editingVendor}
      />

      {/* Deactivate confirmation */}
      <Dialog open={!!confirmDeactivate} onClose={() => setConfirmDeactivate(null)}>
        <DialogTitle>Deactivate Vendor</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deactivating <strong>{confirmDeactivate?.name}</strong> will hide them from the vendor
            dropdown when creating new purchase invoices. Existing invoices are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeactivate(null)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={confirmDeactivateAction}
            disabled={toggleActiveMutation.isPending}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VendorsPage
